import { createContext, memo, type ReactElement, type ReactNode, useContext } from 'react';

import { type Clip } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type AssetDragItem, type BoardDragItem } from '~/components/Shared/Drag/dragTypes';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type HTML5BoardGalleryDragEnrichParams = {
  dataTransfer: DataTransfer;
  isSelected: boolean;
  item: BoardDragItem | AssetDragItem;
  selectedAssets: Clip[];
  selectedBoardCount: number;
};

export type HTML5BoardGalleryDragExtensions = {
  enrichDataTransfer: (params: HTML5BoardGalleryDragEnrichParams) => void;
  onDragEnd: () => void;
};

/** Identity sentinel — missing provider is detected by reference equality, not by calling these. */
const html5BoardGalleryDragExtensionsContextSentinel: HTML5BoardGalleryDragExtensions = {
  enrichDataTransfer: () => {},
  onDragEnd: () => {},
};

/** Explicit no-op implementation for hosts without board agent extensions (tests, nav drag, etc.). */
export const HTML5_BOARD_GALLERY_DRAG_EXTENSIONS_NOOP: HTML5BoardGalleryDragExtensions = {
  enrichDataTransfer: () => {},
  onDragEnd: () => {},
};

const HTML5BoardGalleryDragExtensionsContext = createContext<HTML5BoardGalleryDragExtensions>(
  html5BoardGalleryDragExtensionsContextSentinel,
);

export const HTML5BoardGalleryDragExtensionsProvider = HTML5BoardGalleryDragExtensionsContext.Provider;

export const useHTML5BoardGalleryDragExtensions = (): HTML5BoardGalleryDragExtensions => {
  const context = useContext(HTML5BoardGalleryDragExtensionsContext);

  if (context === html5BoardGalleryDragExtensionsContextSentinel) {
    const error = new Error(
      'useHTML5BoardGalleryDragExtensions must be used within HTML5BoardGalleryDragExtensionsProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: error.message,
    });
  }

  return context;
};

export type HTML5BoardGalleryDragExtensionsMountBoundaryProps = {
  children: ReactNode;
};

/**
 * Supplies {@link HTML5_BOARD_GALLERY_DRAG_EXTENSIONS_NOOP} when no ancestor registered extensions
 * (e.g. boards nav). Skips wrapping when {@link PrivateBoardHTML5DragExtensionsProvider} is above.
 */
export const HTML5BoardGalleryDragExtensionsMountBoundary = memo(
  ({ children }: HTML5BoardGalleryDragExtensionsMountBoundaryProps): ReactElement => {
    const context = useContext(HTML5BoardGalleryDragExtensionsContext);

    if (context === html5BoardGalleryDragExtensionsContextSentinel) {
      return (
        <HTML5BoardGalleryDragExtensionsProvider value={HTML5_BOARD_GALLERY_DRAG_EXTENSIONS_NOOP}>
          {children}
        </HTML5BoardGalleryDragExtensionsProvider>
      );
    }

    return <>{children}</>;
  },
);

HTML5BoardGalleryDragExtensionsMountBoundary.displayName = 'HTML5BoardGalleryDragExtensionsMountBoundary';
